import React, { useState } from "react";
import { Menu, X, Pencil, Check, RotateCcw, Download, Lock } from "lucide-react";
import { useEdit } from "../context/EditContext";
import { useToast } from "../hooks/use-toast";

const links = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#get-involved", label: "Get Involved" },
  { href: "#trustees", label: "Trustees" },
  { href: "#activities", label: "Activities" },
  { href: "#contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { editMode, setEditMode, resetAll, exportJson, adminMode, lockAdmin } = useEdit();
  const { toast } = useToast();

  const toggleEdit = () => {
    setEditMode(!editMode);
    toast({
      title: !editMode ? "Edit mode ON" : "Edit mode OFF",
      description: !editMode
        ? "Click any highlighted text to edit. Changes save automatically."
        : "Your changes are saved locally in this browser.",
    });
  };

  const doReset = () => {
    if (window.confirm("Reset ALL text edits to the original content? This cannot be undone.")) {
      resetAll();
      toast({ title: "Content reset", description: "Original text restored." });
    }
  };

  const doLock = () => {
    if (window.confirm("Lock admin mode? The Edit button will disappear for this browser. You can re-enable by visiting the site with ?edit=1 in the URL.")) {
      lockAdmin();
      toast({ title: "Admin mode locked", description: "Edit controls are now hidden." });
    }
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#dad7c9]/85 border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <a href="#home" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#5c7c2e] text-[#f6efd8] flex items-center justify-center font-bold"
               style={{ fontFamily: "Georgia, serif" }}>SR</div>
          <div className="leading-tight">
            <p className="text-[#3f5520] font-bold text-sm">Sharada Rama Rao</p>
            <p className="text-[#1a1a1a] text-[10px] tracking-widest uppercase">Trust</p>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-[#1a1a1a] text-sm font-medium hover:text-[#c47750] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {adminMode && (
            <>
              {editMode && (
                <>
                  <button
                    onClick={exportJson}
                    title="Export edits as JSON"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium text-[#3f5520] bg-white/70 hover:bg-white transition-colors"
                  >
                    <Download size={14} /> Export
                  </button>
                  <button
                    onClick={doReset}
                    title="Reset all edits"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium text-[#7a2e2e] bg-white/70 hover:bg-white transition-colors"
                  >
                    <RotateCcw size={14} /> Reset
                  </button>
                </>
              )}
              <button
                onClick={toggleEdit}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold shadow-sm transition-colors ${
                  editMode ? "bg-[#5c7c2e] text-white hover:bg-[#4a6425]" : "bg-[#c47750] text-white hover:bg-[#b46540]"
                }`}
              >
                {editMode ? <><Check size={14} /> Done</> : <><Pencil size={14} /> Edit</>}
              </button>
              <button
                onClick={doLock}
                title="Lock admin mode (hides the Edit button)"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium text-[#5a4a3a] bg-white/70 hover:bg-white transition-colors"
              >
                <Lock size={14} />
              </button>
            </>
          )}
        </div>

        <button onClick={() => setOpen((o) => !o)} className="md:hidden text-[#1a1a1a]" aria-label="Toggle menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#dad7c9] border-t border-black/5">
          <nav className="flex flex-col py-2">
            {links.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="px-6 py-3 text-[#1a1a1a] hover:bg-[#cfcdbf]">
                {l.label}
              </a>
            ))}
            <button onClick={toggleEdit} className={`mx-6 my-2 py-2 rounded-full bg-[#c47750] text-white font-semibold flex items-center justify-center gap-1.5 ${adminMode ? "" : "hidden"}`}>
              {editMode ? <><Check size={14}/> Done editing</> : <><Pencil size={14}/> Edit content</>}
            </button>
            {adminMode && editMode && (
              <div className="flex gap-2 px-6 pb-3">
                <button onClick={exportJson} className="flex-1 py-2 rounded-full bg-white text-[#3f5520] text-sm flex items-center justify-center gap-1">
                  <Download size={14} /> Export
                </button>
                <button onClick={doReset} className="flex-1 py-2 rounded-full bg-white text-[#7a2e2e] text-sm flex items-center justify-center gap-1">
                  <RotateCcw size={14} /> Reset
                </button>
                <button onClick={doLock} className="py-2 px-3 rounded-full bg-white text-[#5a4a3a] text-sm flex items-center justify-center gap-1">
                  <Lock size={14} />
                </button>
              </div>
            )}
          </nav>
        </div>
      )}

      {adminMode && editMode && (
        <div className="bg-[#5c7c2e] text-white text-xs text-center py-1.5 px-4">
          Edit mode is ON — click any highlighted text to edit. Press Enter (or click away) to save. Your changes stay in this browser.
        </div>
      )}
    </header>
  );
};

export default Navbar;
